// Generic messages screen — used by students/kids (reply in 1:1, read-only in
// broadcasts) and parents (read-only observer of their kid's threads).
// No "start a new conversation" affordance here: students never initiate and
// parents only observe — a teacher opens every thread. docs/replan-v2.md R10.

import { useT } from '@fajrarabi/i18n/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { ConversationList } from '~/components/messaging/ConversationList';
import { MessageThread } from '~/components/messaging/MessageThread';
import {
  getMessages,
  listConversations,
  markConversationRead,
  replyToConversation,
} from '~/lib/messaging';

export const Route = createFileRoute('/$locale/messages')({
  component: MessagesScreen,
});

function MessagesScreen() {
  const t = useT();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const conversations = useQuery({
    queryKey: ['messages', 'conversations'],
    queryFn: ({ signal }) => listConversations(signal),
    refetchInterval: 15_000,
  });

  const messages = useQuery({
    queryKey: ['messages', 'thread', selectedId],
    queryFn: ({ signal }) => getMessages(selectedId!, signal),
    enabled: selectedId !== null,
    refetchInterval: selectedId ? 10_000 : false,
  });

  function selectConversation(id: string) {
    setSelectedId(id);
    void markConversationRead(id).then(() =>
      qc.invalidateQueries({ queryKey: ['messages', 'conversations'] }),
    );
  }

  const reply = useMutation({
    mutationFn: (body: string) => replyToConversation(selectedId!, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['messages', 'thread', selectedId] });
      await qc.invalidateQueries({ queryKey: ['messages', 'conversations'] });
    },
  });

  const selected = conversations.data?.find((c) => c.id === selectedId) ?? null;
  // Reply only in a 1:1 you're a participant of (not observer, not broadcast).
  const canReply = selected ? selected.kind === '1to1' && !selected.observer : false;

  return (
    <div className="mx-auto max-w-5xl px-6 py-10">
      <h1 className="text-2xl font-bold text-ink-900">{t('app.messages.title')}</h1>
      <div className="mt-6 grid gap-6 md:grid-cols-[20rem_1fr]">
        <div className="rounded-2xl bg-white p-2 ring-1 ring-burgundy-500/10">
          <ConversationList
            conversations={conversations.data ?? []}
            loading={conversations.isLoading}
            selectedId={selectedId}
            onSelect={selectConversation}
          />
        </div>
        <div className="min-h-[24rem] rounded-2xl bg-white p-4 ring-1 ring-burgundy-500/10">
          {selectedId ? (
            <MessageThread
              messages={messages.data ?? []}
              loading={messages.isLoading}
              canReply={canReply}
              sending={reply.isPending}
              error={reply.error}
              onSend={(body) => reply.mutate(body)}
            />
          ) : (
            <p className="grid h-full place-items-center text-sm text-ink-500">
              {t('app.messages.pick_conversation')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
